
import { Modal, Button, Form } from "react-bootstrap"
import { useState, useRef } from "react"
import Select from "react-select"

const KarmaCollectPayment = ({ show, handleClose, onCollectPayment, onSendPaymentLink }) => {
  const [formData, setFormData] = useState({
    karma: null,
    amount: "",
    paymentCategory: null, // New field for Advance Payment / Part Payment
    paymentType: "Online",
    couponCode: "",
    finalAmount: null,
  })

  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSendingLink, setIsSendingLink] = useState(false)
  const [showPaymentOptions, setShowPaymentOptions] = useState(false)
  const [paymentAction, setPaymentAction] = useState(null) // 'collect' or 'send'
  const couponInputRef = useRef()

  const karmaOptions = [
    { value: "Virechan", label: "Virechan" },
    { value: "Vaman", label: "Vaman" },
    { value: "Basti", label: "Basti" },
    { value: "Nasya", label: "Nasya" },
    { value: "Raktamokshana", label: "Raktamokshana" },
  ]

  const paymentCategoryOptions = [
    { value: "advance", label: "Advance Payment" },
    { value: "part", label: "Part Payment" },
    { value: "full", label: "Full Payment" },
  ]

  const paymentModes = [
    { value: "Online", label: "Online" },
    { value: "Bankingcard", label: "Banking Card" },
    { value: "Upi", label: "UPI" },
    { value: "Offline", label: "Cash" },
  ]

  const handlePaymentOptionSelect = (action) => {
    setPaymentAction(action)
    setShowPaymentOptions(true)
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.karma) {
      newErrors.karma = "Please select a karma"
    }

    if (!formData.paymentCategory) {
      newErrors.paymentCategory = "Please select payment category"
    }

    if (!formData.amount || formData.amount.trim() === "") {
      newErrors.amount = "Payment amount is required"
    } else {
      const amount = Number.parseFloat(formData.amount)
      if (isNaN(amount) || amount <= 0) {
        newErrors.amount = "Please enter a valid amount greater than 0"
      }
    }

    if (!formData.paymentType) {
      newErrors.paymentType = "Payment mode is required"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }))
    }
  }

  const handleApplyCoupon = () => {
    const amt = Number.parseFloat(formData.amount)
    if (!amt || amt <= 0) {
      setErrors({ coupon: "Enter a valid amount before applying the coupon." })
      return
    }

    if (formData.couponCode.trim() === "") {
      setErrors({ coupon: "Please enter a valid coupon code." })
      return
    }

    // Set final amount to 0 for any coupon code
    handleInputChange("finalAmount", 0)
    couponInputRef?.current?.blur()
  }

  const handleRemoveCoupon = () => {
    handleInputChange("couponCode", "")
    handleInputChange("finalAmount", null)
  }

  const handleMakePayment = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      const paymentData = {
        karma: formData.karma.value,
        amount: formData.amount,
        paymentCategory: formData.paymentCategory.value,
        paymentType: formData.paymentType,
        couponCode: formData.couponCode,
        finalAmount: formData.finalAmount !== null ? formData.finalAmount : Number.parseFloat(formData.amount),
      }

      if (onCollectPayment) {
        await onCollectPayment(paymentData)
      }

      setFormData({
        karma: null,
        amount: "",
        paymentCategory: null,
        paymentType: "Online",
        couponCode: "",
        finalAmount: null,
      })
      setErrors({})
      setShowPaymentOptions(false)
      handleClose()
    } catch (error) {
      console.error("Payment submission error:", error)
      setErrors({ submit: "Payment failed. Please try again." })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSendPaymentLink = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSendingLink(true)

    try {
      const paymentData = {
        karma: formData.karma.value,
        amount: formData.amount,
        paymentCategory: formData.paymentCategory.value,
        paymentType: formData.paymentType,
        couponCode: formData.couponCode,
        finalAmount: formData.finalAmount !== null ? formData.finalAmount : Number.parseFloat(formData.amount),
      }

      if (onSendPaymentLink) {
        await onSendPaymentLink(paymentData)
      }

      setFormData({
        karma: null,
        amount: "",
        paymentCategory: null,
        paymentType: "Online",
        couponCode: "",
        finalAmount: null,
      })
      setErrors({})
      setShowPaymentOptions(false)
      handleClose()
    } catch (error) {
      console.error("Send payment link error:", error)
      setErrors({ submit: "Failed to send payment link. Please try again." })
    } finally {
      setIsSendingLink(false)
    }
  }

  const handleModalClose = () => {
    setFormData({
      karma: null,
      amount: "",
      paymentCategory: null,
      paymentType: "Online",
      couponCode: "",
      finalAmount: null,
    })
    setErrors({})
    setIsSubmitting(false)
    setIsSendingLink(false)
    setShowPaymentOptions(false)
    setPaymentAction(null)
    handleClose()
  }

  const handleBackToKarmaSelection = () => {
    setFormData({
      karma: null,
      amount: "",
      paymentCategory: null,
      paymentType: "Online",
      couponCode: "",
      finalAmount: null,
    })
    setErrors({})
    setShowPaymentOptions(false)
    setPaymentAction(null)
  }

  const isCouponApplied = formData.finalAmount !== null
  const showCouponSection = formData.paymentType === "Offline"

  return (
    <>
      <Modal show={show} onHide={handleModalClose} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {!showPaymentOptions
              ? "Select Karma"
              : paymentAction
                ? paymentAction === "collect"
                  ? "Collect Payment"
                  : "Send Payment Link"
                : "Payment Options"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!showPaymentOptions ? (
            // Karma Selection Form
            <>
              <div className="mb-3">
                <strong>Remaining Amount: ₹1000</strong>
              </div>

              <Form.Group className="mb-3">
                <Form.Label>
                  Select Karma <span className="text-danger">*</span>
                </Form.Label>
                <Select
                  value={formData.karma}
                  onChange={(selectedOption) => handleInputChange("karma", selectedOption)}
                  options={karmaOptions}
                  placeholder="Search and select karma..."
                  isClearable
                  isSearchable
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      minHeight: "38px",
                      borderColor: errors.karma ? "#dc3545" : state.isFocused ? "#007bff" : "#ced4da",
                      boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 123, 255, 0.25)" : "none",
                    }),
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999,
                    }),
                  }}
                />
                {errors.karma && <div className="text-danger small mt-1">{errors.karma}</div>}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Payment For <span className="text-danger">*</span>
                </Form.Label>
                <Select
                  value={formData.paymentCategory}
                  onChange={(selectedOption) => handleInputChange("paymentCategory", selectedOption)}
                  options={paymentCategoryOptions}
                  placeholder="Select payment category..."
                  isClearable
                  isSearchable
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      minHeight: "38px",
                      borderColor: errors.paymentCategory ? "#dc3545" : state.isFocused ? "#007bff" : "#ced4da",
                      boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 123, 255, 0.25)" : "none",
                    }),
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999,
                    }),
                  }}
                />
                {errors.paymentCategory && <div className="text-danger small mt-1">{errors.paymentCategory}</div>}
              </Form.Group>

              {formData.karma && formData.paymentCategory && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Amount <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.amount}
                      onChange={(e) => handleInputChange("amount", e.target.value)}
                      placeholder="Enter amount"
                      isInvalid={!!errors.amount}
                    />
                    <Form.Control.Feedback type="invalid">{errors.amount}</Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      Payment Mode <span className="text-danger">*</span>
                    </Form.Label>
                    <Select
                      value={paymentModes.find((mode) => mode.value === formData.paymentType) || null}
                      onChange={(selectedOption) =>
                        handleInputChange("paymentType", selectedOption ? selectedOption.value : "")
                      }
                      options={paymentModes}
                      placeholder="Select payment mode..."
                      isClearable
                      isSearchable
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          minHeight: "50px",
                          borderColor: errors.paymentType ? "#dc3545" : state.isFocused ? "#007bff" : "#ced4da",
                          boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 123, 255, 0.25)" : "none",
                        }),
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 9999,
                        }),
                      }}
                    />
                    {errors.paymentType && <div className="text-danger small mt-1">{errors.paymentType}</div>}
                  </Form.Group>

                  {showCouponSection && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>Coupon Code (Optional)</Form.Label>
                        <div className="input-group">
                          <Form.Control
                            type="text"
                            value={formData.couponCode}
                            onChange={(e) => handleInputChange("couponCode", e.target.value)}
                            placeholder="Enter coupon code"
                            disabled={isCouponApplied}
                            ref={couponInputRef}
                          />
                          {!isCouponApplied ? (
                            <Button variant="outline-secondary" onClick={handleApplyCoupon} type="button">
                              Apply
                            </Button>
                          ) : (
                            <Button variant="outline-danger" onClick={handleRemoveCoupon} type="button">
                              Cancel
                            </Button>
                          )}
                        </div>
                        {errors.coupon && <div className="text-danger small mt-1">{errors.coupon}</div>}
                      </Form.Group>

                      {isCouponApplied && (
                        <div className="mb-3">
                          <strong>Final Amount: ₹{formData.finalAmount}</strong>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {formData.karma && formData.paymentCategory && formData.amount && formData.paymentType && (
                <div className="row g-3 mt-3">
                  <div className="col-md-6">
                    <Button
                      variant="primary"
                      className="w-100"
                      onClick={handleMakePayment}
                      disabled={isSubmitting}
                      style={{
                        background: "#007680",
                        border: "none",
                        padding: "12px",
                      }}
                    >
                      <i className="fas fa-credit-card me-2"></i>
                      {isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Processing...
                        </>
                      ) : (
                        "Collect Payment"
                      )}
                    </Button>
                  </div>
                  <div className="col-md-6">
                    <Button
                      variant="info"
                      className="w-100"
                      onClick={() => handlePaymentOptionSelect("send")}
                      style={{
                        padding: "12px",
                      }}
                    >
                      <i className="fas fa-link me-2"></i>
                      Send Payment Link
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : !paymentAction ? (
            // Payment Options Form
            <>
              <div className="mb-4">
                <h6>Karma Details:</h6>
                <p className="mb-3">
                  <strong>Karma:</strong> {formData.karma?.label}
                </p>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <div
                    className="card h-100 border-primary"
                    style={{ cursor: "pointer" }}
                    onClick={() => handlePaymentOptionSelect("collect")}
                  >
                    <div className="card-body text-center">
                      <i className="fas fa-credit-card fa-2x text-primary mb-3"></i>
                      <h5 className="card-title">Make Payment</h5>
                      <p className="card-text text-muted">Collect payment directly from the patient</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div
                    className="card h-100 border-info"
                    style={{ cursor: "pointer" }}
                    onClick={() => handlePaymentOptionSelect("send")}
                  >
                    <div className="card-body text-center">
                      <i className="fas fa-link fa-2x text-info mb-3"></i>
                      <h5 className="card-title">Send Payment Link</h5>
                      <p className="card-text text-muted">Generate and send payment link to patient</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            // Payment Form
            <>
              <div className="mb-3">
                <h6>Payment Summary:</h6>
                <p>
                  <strong>Karma:</strong> {formData.karma?.label}
                </p>
                <p>
                  <strong>Payment Category:</strong> {formData.paymentCategory?.label}
                </p>
                <p>
                  <strong>Amount:</strong> ₹{formData.amount}
                </p>
                <p>
                  <strong>Payment Mode:</strong> {formData.paymentType}
                </p>
                {isCouponApplied && (
                  <p>
                    <strong>Final Amount:</strong> ₹{formData.finalAmount}
                  </p>
                )}
              </div>

              {paymentAction === "send" && (
                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>A payment link will be generated and sent to the patient.
                  The patient can complete the payment remotely.
                </div>
              )}

              {errors.submit && <div className="alert alert-danger">{errors.submit}</div>}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {!showPaymentOptions ? (
            <Button variant="outline-danger" onClick={handleModalClose}>
              Cancel
            </Button>
          ) : !paymentAction ? (
            <>
              <Button variant="outline-info" onClick={handleBackToKarmaSelection}>
                Back
              </Button>
              <Button variant="outline-danger" onClick={handleModalClose}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline-info" onClick={handleBackToKarmaSelection}>
                Back
              </Button>
              <Button variant="outline-danger" onClick={handleModalClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={paymentAction === "collect" ? handleMakePayment : handleSendPaymentLink}
                disabled={isSubmitting || isSendingLink}
                style={{
                  background: "#007680",
                  border: "none",
                }}
              >
                {paymentAction === "collect" ? (
                  isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Processing...
                    </>
                  ) : (
                    "Collect Payment"
                  )
                ) : isSendingLink ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Sending Link...
                  </>
                ) : (
                  "Send Payment Link"
                )}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default KarmaCollectPayment;
